// Administration → Atribute: catalog of reusable metadata fields + the editor modal (also opened from the types page).
import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import type { Attribute, AttrType, ValueList } from '../../types';
import { Combobox, Confirm, EmptyState, Field, Modal, Switch, useToast } from '../../ui';
import { Icon } from '../../ui/Icon';
import { Loading, PageHead, slug, typeLabel } from './common';

export function AttributesPage() {
  const toast = useToast();
  const [items, setItems] = useState<Attribute[] | null>(null);
  const [editing, setEditing] = useState<Attribute | 'new' | null>(null);
  const [del, setDel] = useState<Attribute | null>(null);

  const load = () => api.get<Attribute[]>('/api/catalog/attributes').then(setItems).catch(e => toast(e.message, 'error'));
  useEffect(() => { load(); }, []);

  const remove = async () => {
    if (!del) return;
    try { await api.del('/api/catalog/attributes/' + del.id); toast('Atribut șters', 'ok'); setDel(null); load(); }
    catch (e: any) { toast(e.message, 'error'); }
  };

  return (
    <>
      <PageHead title="Atribute" text="Câmpurile de metadate reutilizabile în tipurile de documente.">
        <button className="btn primary" onClick={() => setEditing('new')}><Icon name="plus" />Atribut nou</button>
      </PageHead>
      {!items ? <Loading /> : items.length === 0 ? <EmptyState icon="tag" title="Niciun atribut" text="Creați primul atribut pentru a-l folosi în tipurile de documente." /> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Nume</th><th>Cod</th><th>Tip</th><th>Listă de valori</th><th>Obligatoriu</th><th>Folosit în</th><th /></tr></thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id} onClick={() => setEditing(a)}>
                  <td><b style={{ fontWeight: 500 }}>{a.name}</b>{a.description && <span className="muted small"> — {a.description}</span>}</td>
                  <td><span className="code">{a.code}</span></td>
                  <td><span className="tag">{typeLabel(a.type)}</span></td>
                  <td>{a.listName || <span className="muted">—</span>}</td>
                  <td>{a.required ? 'Da' : <span className="muted">Nu</span>}</td>
                  <td className="num">{a.usedBy ? `${a.usedBy} ${a.usedBy === 1 ? 'tip' : 'tipuri'}` : <span className="muted">nefolosit</span>}</td>
                  <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <button className="icon-btn" title="Editează" onClick={() => setEditing(a)}><Icon name="edit" /></button>
                    <button className="icon-btn" title="Șterge" onClick={() => setDel(a)} disabled={a.system}><Icon name="trash" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <AttributeEditor attribute={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {del && <Confirm title="Ștergere atribut" danger yesLabel="Șterge" onNo={() => setDel(null)} onYes={remove}
        text={<>Ștergeți atributul <b>{del.name}</b>? {del.usedBy ? `Este folosit în ${del.usedBy} ${del.usedBy === 1 ? 'tip' : 'tipuri'} de documente; ` : ''}Valorile deja salvate în documente rămân, dar câmpul nu va mai fi afișat.</>} />}
    </>
  );
}

interface AttrForm { name: string; code: string; type: AttrType; listId: number | null; required: boolean; description: string; options: Record<string, any> }

/** Modal editor for one attribute. `onSaved` receives the saved attribute (from the server). */
export function AttributeEditor({ attribute, onClose, onSaved }: { attribute: Attribute | null; onClose: () => void; onSaved: (a: Attribute) => void }) {
  const toast = useToast();
  const isNew = !attribute;
  const [f, setF] = useState<AttrForm>({
    name: attribute?.name || '', code: attribute?.code || '', type: attribute?.type || 'TEXT', listId: attribute?.listId ?? null,
    required: !!attribute?.required, description: attribute?.description || '', options: { ...(attribute?.options || {}) },
  });
  const [codeTouched, setCodeTouched] = useState(!isNew);
  const [types, setTypes] = useState<{ code: string; name: string }[]>([]);
  const [lists, setLists] = useState<ValueList[]>([]);
  const [saving, setSaving] = useState(false);
  const set = (p: Partial<AttrForm>) => setF(x => ({ ...x, ...p }));

  useEffect(() => {
    api.get<{ code: string; name: string }[]>('/api/catalog/attribute-types').then(setTypes).catch(e => toast(e.message, 'error'));
    api.get<ValueList[]>('/api/catalog/lists').then(setLists).catch(e => toast(e.message, 'error'));
  }, []);

  const createList = async (name: string) => {
    try {
      const l = await api.post<ValueList>('/api/catalog/lists', { name, allowAdd: true });
      setLists(ls => [...ls, l]);
      toast(`Lista „${l.name}” a fost creată`, 'ok');
      return { value: l.id, label: l.name };
    } catch (e: any) { toast(e.message, 'error'); }
  };

  const save = async () => {
    if (!f.name.trim()) { toast('Introduceți numele atributului', 'error'); return; }
    if (f.type === 'LIST' && !f.listId) { toast('Alegeți lista de valori', 'error'); return; }
    setSaving(true);
    try {
      const body = { name: f.name.trim(), code: f.code.trim() || undefined, type: f.type, listId: f.type === 'LIST' ? f.listId : null, required: f.required, description: f.description.trim() || null, options: f.options };
      const r = isNew ? await api.post<Attribute>('/api/catalog/attributes', body) : await api.put<Attribute>('/api/catalog/attributes/' + attribute!.id, body);
      toast(isNew ? 'Atribut creat' : 'Atribut salvat', 'ok');
      onSaved(r);
    } catch (e: any) { toast(e.message, 'error'); } finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'Atribut nou' : `Atribut: ${attribute!.name}`} icon="tag" onClose={onClose}
      footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button><button className="btn primary" disabled={saving} onClick={save}>{saving ? 'Se salvează…' : 'Salvează'}</button></>}>
      <div className="form-grid">
        <Field label="Nume" required><input className="input" autoFocus value={f.name} onChange={e => set({ name: e.target.value, ...(codeTouched ? {} : { code: slug(e.target.value) }) })} placeholder="ex. Număr contract" /></Field>
        <Field label="Cod" hint="Identificator tehnic, folosit în API și la sortare"><input className="input" value={f.code} onChange={e => { setCodeTouched(true); set({ code: slug(e.target.value) || e.target.value }); }} disabled={!!attribute?.system} /></Field>
        <Field label="Tip" required>
          <select className="select" value={f.type} onChange={e => set({ type: e.target.value as AttrType })} disabled={!!attribute?.system}>
            {(types.length ? types : [{ code: f.type, name: typeLabel(f.type) }]).map(t => <option key={t.code} value={t.code}>{typeLabel(t.code)}</option>)}
          </select>
        </Field>
        {f.type === 'LIST' ? (
          <Field label="Listă de valori" required hint="Tastați un nume nou pentru a crea o listă pe loc">
            <Combobox value={f.listId} options={lists.map(l => ({ value: l.id, label: l.name, sub: l.itemCount != null ? `${l.itemCount} valori` : undefined }))} onChange={v => set({ listId: v == null ? null : Number(v) })} allowAdd onAdd={createList} placeholder="Alegeți lista sau creați una nouă…" />
          </Field>
        ) : <div />}
        <Field label="Descriere" className="span2"><input className="input" value={f.description} onChange={e => set({ description: e.target.value })} placeholder="Explicație scurtă afișată utilizatorilor (opțional)" /></Field>
        <Field><Switch on={f.required} onChange={v => set({ required: v })} label="Obligatoriu implicit" /></Field>
        {f.type === 'DATE' && <Field><Switch on={!!f.options.defaultToday} onChange={v => set({ options: { ...f.options, defaultToday: v } })} label="Implicit data de azi" /></Field>}
        {f.type === 'LIST' && f.listId != null && <DefaultValue listId={f.listId} value={f.options.defaultValue ?? ''} onChange={v => set({ options: { ...f.options, defaultValue: v || undefined } })} />}
      </div>
    </Modal>
  );
}

/** "Valoare implicită" for a list attribute: pre-selected when a new document is registered (can be changed). */
function DefaultValue({ listId, value, onChange }: { listId: number; value: string; onChange: (v: string) => void }) {
  const [items, setItems] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => { api.get<ValueList>('/api/catalog/lists/' + listId).then(l => setItems((l.items || []).filter(i => i.active).map(i => ({ value: i.value, label: i.label || i.value })))).catch(() => setItems([])); }, [listId]);
  return (
    <Field label="Valoare implicită" hint="Se completează automat la înregistrarea unui document nou; poate fi schimbată">
      <select className="select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">— fără —</option>
        {items.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
        {value && !items.some(i => i.value === value) && <option value={value}>{value}</option>}
      </select>
    </Field>
  );
}
